import React from 'react'
import { Select } from '@surecompanies/core_components'
import {
  getPaymentAssignmentStatusLabel,
  PaymentAssignmentStatusId,
  PaymentType,
  Priority,
  PriorityList,
  TerminatedFilter,
  PAYMENT_TYPE_LABELS,
  PRIORITY_LABELS,
  PersonRelationshipEnum,
  PERSON_RELATIONSHIP_LABELS,
} from '../Utils/types'
import { PaymentTypeEnum } from 'types/benefit'
import useFeatureFlags from 'hooks/useFeatureFlags'
import {
  FilterSection,
  FilterSectionTitle,
  FilterGroup,
  FilterLabel,
} from './styles'
import { toggleSelectAllInScope } from './helpers'
import SelectAllHeader from './SelectAllHeader'
import PaymentAssignmentFilterUserSelect from './PaymentAssignmentFilterUserSelect'
import type { FilterUpdateFn, UserOption } from './types'

const ALL_PRIMARY_PAYMENT_METHOD_OPTIONS = [
  { label: PaymentTypeEnum.VCN, value: PaymentTypeEnum.VCN },
  { label: PaymentTypeEnum.ACH, value: PaymentTypeEnum.ACH },
  { label: PaymentTypeEnum.CHECK, value: PaymentTypeEnum.CHECK },
  { label: PaymentTypeEnum.GROUP_ACH, value: PaymentTypeEnum.GROUP_ACH },
]

const RELATIONSHIP_OPTIONS = Object.values(PersonRelationshipEnum).map(
  (value) => ({
    label: PERSON_RELATIONSHIP_LABELS[value],
    value,
  })
)

interface DrawerSectionPaymentStatusProps {
  statusOptions: PaymentAssignmentStatusId[]
  statusesValue: string[]
  updateFilter: FilterUpdateFn
  paymentTypes: string[]
  paymentTypesValue: (string | PaymentType)[]
  paymentTypeScope: string[]
  setPaymentTypeSearch: (v: string) => void
  priorityLevelsValue: Priority[]
  priorityScope: Priority[]
  setPrioritySearch: (v: string) => void
  paymentSubmittedByIdsValue: number[]
  paymentSubmittedByOptions: UserOption[]
  usersLoading: boolean
  checkersLoading: boolean
  terminatedFilterValue: TerminatedFilter
  relationshipsValue: PersonRelationshipEnum[]
  binderPaymentValue: boolean | undefined
  primaryPaymentMethodsValue: string[]
}

export default function DrawerSectionPaymentStatus({
  statusOptions,
  statusesValue,
  updateFilter,
  paymentTypes,
  paymentTypesValue,
  paymentTypeScope,
  setPaymentTypeSearch,
  priorityLevelsValue,
  priorityScope,
  setPrioritySearch,
  paymentSubmittedByIdsValue,
  paymentSubmittedByOptions,
  usersLoading,
  checkersLoading,
  terminatedFilterValue,
  relationshipsValue,
  binderPaymentValue,
  primaryPaymentMethodsValue,
}: DrawerSectionPaymentStatusProps) {
  const { flags } = useFeatureFlags()
  const primaryPaymentMethodOptions = ALL_PRIMARY_PAYMENT_METHOD_OPTIONS.filter(
    (opt) => {
      if (opt.value === PaymentTypeEnum.CHECK) {
        return flags['admin.finance.payment-coordination-enable-check']
      }
      if (opt.value === PaymentTypeEnum.GROUP_ACH) {
        return flags['admin.finance.payment-coordination-enable-group-ach']
      }
      return true
    }
  )
  return (
    <FilterSection>
      <FilterSectionTitle>Payment Status</FilterSectionTitle>

      <FilterGroup>
        <FilterLabel>Status</FilterLabel>
        <Select
          mode="multiple"
          placeholder="All Statuses"
          value={statusesValue}
          showSearch
          optionFilterProp="children"
          filterOption={(input, option: any) => {
            const label = option?.children
            return typeof label === 'string'
              ? label.toLowerCase().includes(input.toLowerCase())
              : false
          }}
          onChange={(value) => {
            if (Array.isArray(value)) {
              updateFilter('statuses', value)
            }
          }}
          style={{ width: '100%' }}
          maxTagCount={statusesValue.length >= 2 ? 0 : 'responsive'}
          maxTagPlaceholder={
            statusesValue.length >= 2
              ? (omittedValues) => `${omittedValues.length} items selected`
              : undefined
          }
        >
          {statusOptions.map((status) => (
            <Select.Option key={status} value={status}>
              {getPaymentAssignmentStatusLabel(status)}
            </Select.Option>
          ))}
        </Select>
      </FilterGroup>

      <FilterGroup>
        <FilterLabel>Payment Type</FilterLabel>
        <Select
          mode="multiple"
          placeholder="All Types"
          value={paymentTypesValue}
          showSearch
          onSearch={setPaymentTypeSearch}
          optionFilterProp="children"
          filterOption={(input, option: any) => {
            const label = option?.children
            return typeof label === 'string'
              ? label.toLowerCase().includes(input.toLowerCase())
              : false
          }}
          dropdownRender={(menu) => (
            <>
              <SelectAllHeader
                selectedCount={paymentTypesValue.length}
                scopeValues={paymentTypeScope}
                currentValues={paymentTypesValue as string[]}
                onToggle={() => {
                  const current = Array.isArray(paymentTypesValue)
                    ? (paymentTypesValue as string[])
                    : []
                  const next = toggleSelectAllInScope(current, paymentTypeScope)
                  updateFilter('paymentTypes', next as PaymentType[])
                }}
              />
              {menu}
            </>
          )}
          onChange={(value) => {
            if (Array.isArray(value)) {
              updateFilter('paymentTypes', value as PaymentType[])
            }
          }}
          style={{ width: '100%' }}
          maxTagCount={
            (paymentTypesValue as string[]).length >= 2 ? 0 : 'responsive'
          }
          maxTagPlaceholder={
            (paymentTypesValue as string[]).length >= 2
              ? (omittedValues) => `${omittedValues.length} items selected`
              : undefined
          }
        >
          {paymentTypes.map((type) => (
            <Select.Option key={type} value={type}>
              {PAYMENT_TYPE_LABELS[type] ?? type}
            </Select.Option>
          ))}
        </Select>
      </FilterGroup>

      <FilterGroup>
        <FilterLabel>Primary Payment Method</FilterLabel>
        <Select
          mode="multiple"
          placeholder="All Methods"
          value={primaryPaymentMethodsValue}
          onChange={(value) => {
            if (Array.isArray(value)) {
              updateFilter('primaryPaymentMethods', value)
            }
          }}
          style={{ width: '100%' }}
          options={primaryPaymentMethodOptions}
          maxTagCount={
            primaryPaymentMethodsValue.length >= 2 ? 0 : 'responsive'
          }
          maxTagPlaceholder={
            primaryPaymentMethodsValue.length >= 2
              ? (omittedValues) => `${omittedValues.length} items selected`
              : undefined
          }
        />
      </FilterGroup>

      <FilterGroup>
        <FilterLabel>Priority</FilterLabel>
        <Select
          mode="multiple"
          placeholder="All (default)"
          value={priorityLevelsValue}
          showSearch
          onSearch={setPrioritySearch}
          dropdownRender={(menu) => (
            <>
              <SelectAllHeader
                selectedCount={priorityLevelsValue.length}
                scopeValues={priorityScope}
                currentValues={priorityLevelsValue as Priority[]}
                onToggle={() => {
                  const current = Array.isArray(priorityLevelsValue)
                    ? (priorityLevelsValue as Priority[])
                    : []
                  const next = toggleSelectAllInScope(current, priorityScope)
                  updateFilter('priorityLevels', next as Priority[])
                }}
              />
              {menu}
            </>
          )}
          filterOption={(input, option: any) => {
            const haystack =
              (typeof option?.label === 'string' ? option.label : '') ||
              (typeof option?.children === 'string' ? option.children : '') ||
              (typeof option?.['data-search'] === 'string'
                ? option['data-search']
                : '') ||
              (typeof option?.props?.['data-search'] === 'string'
                ? option.props['data-search']
                : '')
            return haystack.toLowerCase().includes(input.toLowerCase())
          }}
          onChange={(value) => {
            const next = (Array.isArray(value) ? value : [])
              .map((v) => Number(v))
              .filter((n) => Number.isFinite(n)) as Priority[]
            updateFilter('priorityLevels', next)
          }}
          style={{ width: '100%' }}
          maxTagCount={
            (priorityLevelsValue as number[]).length >= 2 ? 0 : 'responsive'
          }
          maxTagPlaceholder={
            (priorityLevelsValue as number[]).length >= 2
              ? (omittedValues) => `${omittedValues.length} selected`
              : undefined
          }
        >
          {PriorityList.map((priority) => (
            <Select.Option
              key={priority}
              value={priority}
              data-search={`${PRIORITY_LABELS[priority] ?? ''} ${priority}`}
            >
              {PRIORITY_LABELS[priority] ?? String(priority)}
            </Select.Option>
          ))}
        </Select>
      </FilterGroup>

      <PaymentAssignmentFilterUserSelect
        label="Payment submitted by"
        value={paymentSubmittedByIdsValue}
        onChange={(ids) => updateFilter('paymentSubmittedByIds', ids)}
        options={paymentSubmittedByOptions}
        loading={usersLoading || checkersLoading}
      />

      <FilterGroup>
        <FilterLabel>Employment status</FilterLabel>
        <Select<TerminatedFilter>
          value={terminatedFilterValue}
          onChange={(value) => updateFilter('terminatedFilter', value)}
          style={{ width: '100%' }}
        >
          <Select.Option value={TerminatedFilter.NOT_TERMINATED}>
            Not terminated
          </Select.Option>
          <Select.Option value={TerminatedFilter.TERMINATED}>
            Terminated
          </Select.Option>
          <Select.Option value={TerminatedFilter.ALL}>All</Select.Option>
        </Select>
      </FilterGroup>

      <FilterGroup>
        <FilterLabel>relation</FilterLabel>
        <Select<PersonRelationshipEnum[]>
          mode="multiple"
          allowClear
          value={relationshipsValue}
          onChange={(value) => updateFilter('relationships', value)}
          style={{ width: '100%' }}
          placeholder="All relations"
          maxTagCount="responsive"
          options={RELATIONSHIP_OPTIONS}
        />
      </FilterGroup>

      <FilterGroup>
        <FilterLabel>Binder payment</FilterLabel>
        <Select<string>
          value={
            binderPaymentValue === undefined
              ? 'all'
              : binderPaymentValue
                ? 'true'
                : 'false'
          }
          onChange={(value) => {
            updateFilter(
              'binderPayment',
              value === 'all' ? undefined : value === 'true'
            )
          }}
          style={{ width: '100%' }}
        >
          <Select.Option value="all">All</Select.Option>
          <Select.Option value="true">Yes</Select.Option>
          <Select.Option value="false">No</Select.Option>
        </Select>
      </FilterGroup>
    </FilterSection>
  )
}
